import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';

export interface EmailTemplate {
  subject: string;
  html: string;
  text?: string;
}

export interface EmailOptions {
  to: string | string[];
  subject: string;
  html?: string;
  text?: string;
  from?: string;
  replyTo?: string;
  cc?: string | string[];
  bcc?: string | string[];
  attachments?: EmailAttachment[];
  templateId?: string;
  templateData?: Record<string, any>;
}

export interface EmailAttachment {
  filename: string;
  content: string | Buffer;
  contentType?: string;
}

class EmailService {
  private provider: 'resend' | 'sendgrid' | 'ses' | 'smtp' = 'resend';
  private apiKey: string | null = null;
  private fromEmail: string = 'noreply@sphericalmanager.com';
  private fromName: string = 'Spherical Manager';

  constructor() {
    // Initialize based on environment variables
    this.apiKey = import.meta.env.VITE_EMAIL_API_KEY || null;
    this.provider = (import.meta.env.VITE_EMAIL_PROVIDER as any) || 'resend';
    this.fromEmail = import.meta.env.VITE_EMAIL_FROM || this.fromEmail;
    this.fromName = import.meta.env.VITE_EMAIL_FROM_NAME || this.fromName;
  }

  /**
   * Send an email using the configured provider
   */
  async send(options: EmailOptions): Promise<{ id: string; success: boolean }> {
    try {
      // Validate email configuration
      if (!this.apiKey) {
        throw new Error('Email service not configured. Please set VITE_EMAIL_API_KEY');
      }

      // Log email activity
      await this.logEmailActivity(options);

      // Send based on provider
      switch (this.provider) {
        case 'resend':
          return await this.sendViaResend(options);
        case 'sendgrid':
          return await this.sendViaSendGrid(options);
        case 'ses':
          return await this.sendViaSES(options);
        case 'smtp':
          return await this.sendViaSMTP(options);
        default:
          throw new Error(`Unsupported email provider: ${this.provider}`);
      }
    } catch (error) {
      logError('Failed to send email', error, { to: options.to, subject: options.subject });
      throw error;
    }
  }

  /**
   * Send email via Resend
   */
  private async sendViaResend(options: EmailOptions): Promise<{ id: string; success: boolean }> {
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        from: options.from || `${this.fromName} <${this.fromEmail}>`,
        to: Array.isArray(options.to) ? options.to : [options.to],
        subject: options.subject,
        html: options.html,
        text: options.text,
        reply_to: options.replyTo,
        cc: options.cc,
        bcc: options.bcc,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Resend API error: ${error.message || response.statusText}`);
    }

    const data = await response.json();
    return { id: data.id, success: true };
  }

  /**
   * Send email via SendGrid
   */
  private async sendViaSendGrid(options: EmailOptions): Promise<{ id: string; success: boolean }> {
    const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        personalizations: [
          {
            to: Array.isArray(options.to)
              ? options.to.map((email) => ({ email }))
              : [{ email: options.to }],
            cc: options.cc
              ? Array.isArray(options.cc)
                ? options.cc.map((email) => ({ email }))
                : [{ email: options.cc }]
              : undefined,
            bcc: options.bcc
              ? Array.isArray(options.bcc)
                ? options.bcc.map((email) => ({ email }))
                : [{ email: options.bcc }]
              : undefined,
          },
        ],
        from: {
          email: options.from?.includes('<')
            ? options.from.match(/<(.+)>/)?.[1] || this.fromEmail
            : options.from || this.fromEmail,
          name: options.from?.includes('<') ? options.from.split('<')[0].trim() : this.fromName,
        },
        subject: options.subject,
        content: [
          ...(options.text ? [{ type: 'text/plain', value: options.text }] : []),
          ...(options.html ? [{ type: 'text/html', value: options.html }] : []),
        ],
        reply_to: options.replyTo ? { email: options.replyTo } : undefined,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`SendGrid API error: ${error.errors?.[0]?.message || response.statusText}`);
    }

    // SendGrid returns 202 with X-Message-Id header
    const messageId = response.headers.get('X-Message-Id') || 'success';
    return { id: messageId, success: true };
  }

  /**
   * Send email via AWS SES (placeholder)
   */
  private async sendViaSES(options: EmailOptions): Promise<{ id: string; success: boolean }> {
    // This would require AWS SDK integration
    throw new Error('AWS SES integration not yet implemented');
  }

  /**
   * Send email via SMTP (placeholder)
   */
  private async sendViaSMTP(options: EmailOptions): Promise<{ id: string; success: boolean }> {
    // This would require nodemailer or similar
    throw new Error('SMTP integration not yet implemented');
  }

  /**
   * Log email activity to database
   */
  private async logEmailActivity(options: EmailOptions): Promise<void> {
    try {
      const { error } = await supabase.from('email_logs').insert({
        to: Array.isArray(options.to) ? options.to.join(', ') : options.to,
        subject: options.subject,
        status: 'pending',
        provider: this.provider,
        metadata: {
          cc: options.cc,
          bcc: options.bcc,
          has_attachments: !!options.attachments?.length,
          template_id: options.templateId,
        },
      });

      if (error) {
        logError('Failed to log email activity', error);
      }
    } catch (error) {
      // Don't throw - logging failure shouldn't prevent email sending
      logError('Failed to log email activity', error);
    }
  }

  /**
   * Send a user invitation email
   */
  async sendInvitation(params: {
    email: string;
    inviterName: string;
    organizationName: string;
    role: string;
    inviteUrl: string;
  }): Promise<{ id: string; success: boolean }> {
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #1a73e8; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .button { display: inline-block; padding: 12px 24px; background-color: #1a73e8; color: white; text-decoration: none; border-radius: 4px; margin: 20px 0; }
            .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Welcome to Spherical Manager</h1>
            </div>
            <div class="content">
              <h2>You've been invited!</h2>
              <p>Hi there,</p>
              <p>${params.inviterName} has invited you to join <strong>${params.organizationName}</strong> as a <strong>${params.role}</strong> on Spherical Manager.</p>
              <p>Click the button below to accept the invitation and set up your account:</p>
              <div style="text-align: center;">
                <a href="${params.inviteUrl}" class="button">Accept Invitation</a>
              </div>
              <p>Or copy and paste this link into your browser:</p>
              <p style="word-break: break-all; color: #1a73e8;">${params.inviteUrl}</p>
              <p>This invitation will expire in 7 days.</p>
            </div>
            <div class="footer">
              <p>&copy; 2024 Spherical Manager. All rights reserved.</p>
              <p>If you didn't expect this invitation, you can safely ignore this email.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const text = `
You've been invited to Spherical Manager!

${params.inviterName} has invited you to join ${params.organizationName} as a ${params.role}.

Accept the invitation here: ${params.inviteUrl}

This invitation will expire in 7 days.

If you didn't expect this invitation, you can safely ignore this email.
    `;

    return this.send({
      to: params.email,
      subject: `You've been invited to join ${params.organizationName}`,
      html,
      text,
    });
  }

  /**
   * Send a password reset email
   */
  async sendPasswordReset(params: {
    email: string;
    resetUrl: string;
    userName?: string;
  }): Promise<{ id: string; success: boolean }> {
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #1a73e8; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .button { display: inline-block; padding: 12px 24px; background-color: #1a73e8; color: white; text-decoration: none; border-radius: 4px; margin: 20px 0; }
            .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Password Reset Request</h1>
            </div>
            <div class="content">
              <p>Hi${params.userName ? ` ${params.userName}` : ''},</p>
              <p>We received a request to reset your password for Spherical Manager.</p>
              <p>Click the button below to reset your password:</p>
              <div style="text-align: center;">
                <a href="${params.resetUrl}" class="button">Reset Password</a>
              </div>
              <p>Or copy and paste this link into your browser:</p>
              <p style="word-break: break-all; color: #1a73e8;">${params.resetUrl}</p>
              <p>This link will expire in 1 hour for security reasons.</p>
              <p>If you didn't request a password reset, please ignore this email and your password will remain unchanged.</p>
            </div>
            <div class="footer">
              <p>&copy; 2024 Spherical Manager. All rights reserved.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const text = `
Password Reset Request

Hi${params.userName ? ` ${params.userName}` : ''},

We received a request to reset your password for Spherical Manager.

Reset your password here: ${params.resetUrl}

This link will expire in 1 hour for security reasons.

If you didn't request a password reset, please ignore this email and your password will remain unchanged.
    `;

    return this.send({
      to: params.email,
      subject: 'Reset your Spherical Manager password',
      html,
      text,
    });
  }

  /**
   * Send activity notification email
   */
  async sendActivityNotification(params: {
    email: string;
    userName: string;
    activityType: string;
    activityDescription: string;
    entityName: string;
    entityType: 'organization' | 'contact' | 'opportunity';
    activityUrl: string;
  }): Promise<{ id: string; success: boolean }> {
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #1a73e8; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background-color: #f9f9f9; }
            .activity-box { background: white; border: 1px solid #ddd; padding: 15px; margin: 15px 0; border-radius: 4px; }
            .button { display: inline-block; padding: 10px 20px; background-color: #1a73e8; color: white; text-decoration: none; border-radius: 4px; }
            .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Activity Update</h1>
            </div>
            <div class="content">
              <p>Hi ${params.userName},</p>
              <p>A new activity has been logged:</p>
              <div class="activity-box">
                <p><strong>Type:</strong> ${params.activityType}</p>
                <p><strong>${params.entityType}:</strong> ${params.entityName}</p>
                <p><strong>Description:</strong> ${params.activityDescription}</p>
              </div>
              <div style="text-align: center; margin: 20px 0;">
                <a href="${params.activityUrl}" class="button">View Activity</a>
              </div>
            </div>
            <div class="footer">
              <p>&copy; 2024 Spherical Manager. All rights reserved.</p>
              <p>You're receiving this because you're following ${params.entityName}.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    return this.send({
      to: params.email,
      subject: `New ${params.activityType} activity for ${params.entityName}`,
      html,
    });
  }
}

// Export singleton instance
export const emailService = new EmailService();
